import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingAction {
  readonly id: string;
  readonly action_type: string;
  readonly action_payload: Record<string, unknown>;
  readonly maker_id: string;
  readonly maker_department: string;
  readonly maker_name: string;
  readonly status: "pending" | "approved" | "rejected" | "expired";
  readonly created_at: string;
  readonly expires_at: string;
  readonly affected_count: number;
  readonly diff_preview: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly before: Record<string, unknown>;
    readonly after: Record<string, unknown>;
  }>;
}

export interface PendingApprovalsResponse {
  readonly items: readonly PendingAction[];
  readonly total: number;
}

// ---------------------------------------------------------------------------
// API module
// ---------------------------------------------------------------------------

interface PendingApprovalsEnvelope {
  readonly ok: true;
  readonly data: readonly PendingAction[];
  readonly pagination?: { readonly total: number };
}

function isEnvelope(
  body: PendingApprovalsResponse | PendingApprovalsEnvelope,
): body is PendingApprovalsEnvelope {
  return (
    (body as PendingApprovalsEnvelope).ok === true &&
    Array.isArray((body as PendingApprovalsEnvelope).data)
  );
}

export const pendingApprovalsApi = {
  /** List pending approvals visible to the current admin
   * (Direction and eligible checkers only — enforced server-side).
   *
   * The server returns the canonical envelope `{ ok, data, pagination }`
   * (see rules/common/patterns.md). Older callers still expected
   * `{ items, total }`, so we normalize here rather than forcing every
   * consumer to change. Without this, the dashboard widget crashed on
   * `data.items.filter(...)` because `items` was undefined. */
  list: async (): Promise<PendingApprovalsResponse> => {
    const raw = await adminFetch<
      PendingApprovalsResponse | PendingApprovalsEnvelope
    >("/api/admin/products/pending-approvals");
    if (isEnvelope(raw)) {
      return { items: raw.data, total: raw.pagination?.total ?? raw.data.length };
    }
    return raw;
  },

  /** Approve a pending action. Optional notes are persisted in the audit log. */
  approve: (actionId: string, notes?: string) =>
    adminFetch<{ ok: true }>(
      `/api/admin/products/pending-approvals/${actionId}/approve`,
      {
        method: "POST",
        body: JSON.stringify({ notes: notes ?? null }),
      },
    ),

  /** Reject a pending action with a required reason (shown to the maker). */
  reject: (actionId: string, reason: string) =>
    adminFetch<{ ok: true }>(
      `/api/admin/products/pending-approvals/${actionId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ reason }),
      },
    ),
} as const;
